import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

export default function useLazyQueryPaginated(query, options) {
  const { skip, variables, ...otherOptions } = options;

  const lastOffsetCount = useRef(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isFinished, setIsFinished] = useState(false);

  const [
    getData,
    {
      called,
      data: queryData,
      error: queryError,
      loading,
      fetchMore,
      refetch,
      updateQuery,
    },
  ] = useLazyQuery(query, {
    variables: {
      ...variables,
      offset: 0,
    },
    ...otherOptions,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });

  const refetchAll = useCallback(async () => {
    if (!skip) {
      try {
        const { data } = await refetch({ offset: 0 });
        // update the cache manually with the new result
        updateQuery(() => data);
      } catch (error) {
        console.error('cannot refetch paginated query', error);
      }
    }
  }, [skip, refetch, updateQuery]);

  const getNewItemCount = useCallback((data) => {
    return data
      ? Math.max(...Object.keys(data).map((k) => data[k].edges.length))
      : 0;
  }, []);

  const fetchMoreData = useCallback(() => {
    let newOffsetCount = getNewItemCount(queryData);
    if (
      newOffsetCount > 0 &&
      lastOffsetCount.current !== newOffsetCount &&
      typeof fetchMore === 'function' // fetchMore won't exist if the query function returns no results, since there is no more to fetch
    ) {
      fetchMore({
        variables: {
          offset: newOffsetCount,
        },
      }).then(({ data }) => {
        const fetchedOffsetCount = getNewItemCount(data);
        lastOffsetCount.current = newOffsetCount;

        if (fetchedOffsetCount === 0) {
          setIsFinished(true);
        }
      });
    } else {
      setIsFinished(true);
    }
  }, [queryData, getNewItemCount, fetchMore]);

  useEffect(
    function handleGetData() {
      if (skip) {
        return;
      }

      if (!queryData && !called) {
        getData();
      }
    },
    [skip, queryData, called, getData],
  );

  useEffect(
    function handleFetchMoreData() {
      if (queryData && !loading && !isFinished && !skip) {
        fetchMoreData();
      }
    },
    [queryData, loading, isFinished, fetchMoreData, skip],
  );

  useEffect(
    function handleSetData() {
      if (queryData) {
        setData((prevData) => ({ ...prevData, ...queryData }));
      }
    },
    [queryData],
  );

  useEffect(
    function handleSetError() {
      if (queryError) {
        setError(queryError);
      }
    },
    [queryError],
  );

  return {
    data,
    error,
    isFinished,
    refetch: refetchAll,
  };
}
